import {NotificationState, NotificationStateList} from "../definitions/Config";

/**
 * Factory Class for Notification List
 * Uses Immutable pattern: always generating a new state from given state(s)
 */
export default class StateListFactory {

    /**
     * Get the default state
     *
     * @returns {NotificationState}
     */
    static getDefaultState(): NotificationState {
        return {
            type: null,
            counter: Date.now(),
            priority: true,

            read: false,
            dismissed: false,
            deleted: false,

            payload: {},
        };
    }

    /**
     * Delivers the current state with standard values for non-given parameters
     *
     * @param   {NotificationState} notificationState
     * @returns {NotificationState}
     */
    static getState(notificationState: NotificationState): NotificationState {
        return Object.assign({},
            this.getDefaultState,
            notificationState
        );
    }

    /**
     * Delivers the updated Notification state
     * -> Returns the last updated one of the two if `useNewest` is set to true
     * -> Returns the second one merged on top of the first one otherwise
     *
     * @param   {NotificationState} notificationState1
     * @param   {NotificationState} notificationState2
     * @param   {boolean} useNewest
     * @returns {NotificationState}
     */
    static getNewState(
        notificationState1: NotificationState,
        notificationState2: NotificationState,
        useNewest = true
    ): NotificationState {

        let oldNotificationState: NotificationState;
        let newNotificationState: NotificationState;

        if (useNewest) {
            if (notificationState1.counter < notificationState2.counter) {
                oldNotificationState = notificationState1;
                newNotificationState = notificationState2;

            } else if (notificationState1.counter > notificationState2.counter) {
                oldNotificationState = notificationState2;
                newNotificationState = notificationState1;

            } else if (notificationState1.priority) {
                oldNotificationState = notificationState2;
                newNotificationState = notificationState1;

            } else {
                oldNotificationState = notificationState1;
                newNotificationState = notificationState2;
            }
        } else {
            oldNotificationState = notificationState1;
            newNotificationState = notificationState2;
        }
        newNotificationState.counter = Date.now();

        return Object.assign({},
            this.getDefaultState(),
            oldNotificationState,
            newNotificationState
        );
    }

    /**
     * Compares two Notification states
     * -> Returns true if they are not identical
     *
     * @param   {NotificationState} notificationState1
     * @param   {NotificationState} notificationState2
     * @returns {boolean}
     */
    static hasStateChanged(
        notificationState1: NotificationState,
        notificationState2: NotificationState
    ): boolean {

        let hasChanged = false;

        if (notificationState1.read !== notificationState2.read) {
            hasChanged = true;
        }
        if (notificationState1.dismissed !== notificationState2.dismissed) {
            hasChanged = true;
        }
        if (notificationState1.deleted !== notificationState2.deleted) {
            hasChanged = true;
        }

        let oldPayload = notificationState1.payload
            ? JSON.stringify(notificationState1.payload)
            : "";
        let newPayload = notificationState2.payload
            ? JSON.stringify(notificationState2.payload)
            : "";
        if (oldPayload !== newPayload) {
            hasChanged = true;
        }

        return hasChanged;
    }

    /**
     * Returns the number of existing notification states (= non-deleted) in the list
     * Optional: only read notifications / dismissed notifications / both
     *
     * @param   {NotificationStateList} notificationStateList
     * @param   {boolean} read
     * @param   {boolean} dismissed
     * @returns {number}
     */
    static count(notificationStateList: NotificationStateList, read?: boolean, dismissed?: boolean): number {
        let count = 0;

        notificationStateList.forEach((notificationState: NotificationState) => {
            if (notificationState.deleted) {
                return;
            }
            if (typeof read !== "undefined" && notificationState.read !== read) {
                return;
            }
            if (typeof dismissed !== "undefined" && notificationState.dismissed !== dismissed) {
                return;
            }
            count ++;
        });
        return count;
    }

    /**
     * Generates the new list with added elements
     *
     * @param   {NotificationStateList} currentNotificationStateList
     * @param   {NotificationStateList} newNotificationStateList
     * @returns {NotificationStateList}
     */
    static push(
        currentNotificationStateList: NotificationStateList,
        newNotificationStateList: NotificationStateList
    ): NotificationStateList {

        let notificationStateList = new Map<number, NotificationState>();

        currentNotificationStateList.forEach((currentNotificationState: NotificationState) => {
            notificationStateList.set(
                currentNotificationState.type,
                this.getState(
                    currentNotificationState
                )
            );
        });
        newNotificationStateList.forEach((newNotificationState: NotificationState) => {
            if (notificationStateList.has(newNotificationState.type)) {
                notificationStateList.set(
                    newNotificationState.type,
                    this.getNewState(
                        currentNotificationStateList.get(newNotificationState.type),
                        newNotificationState,
                        false
                    )
                );
            } else {
                notificationStateList.set(
                    newNotificationState.type,
                    this.getState(
                        newNotificationState
                    )
                );
            }
        });
        return notificationStateList;
    }

    /**
     * Generates the new list updated with a given other list
     *    - Entries which don't exist anymore will be deleted
     *    - Non-existing entries will be added
     *    - Already existing entries will be merged
     *
     * @param   {NotificationStateList} currentNotificationStateList
     * @param   {NotificationStateList} newNotificationStateList
     * @returns {NotificationStateList}
     */
    static update(
        currentNotificationStateList: NotificationStateList,
        newNotificationStateList: NotificationStateList
    ): NotificationStateList {

        let notificationStateList = new Map<number, NotificationState>();

        currentNotificationStateList.forEach((currentNotificationState: NotificationState) => {
            if (newNotificationStateList.has(currentNotificationState.type)) {
                notificationStateList.set(
                    currentNotificationState.type,
                    this.getNewState(
                        currentNotificationState,
                        newNotificationStateList.get(currentNotificationState.type)
                    )
                );
            }
        });
        newNotificationStateList.forEach((newNotificationState: NotificationState) => {
            if (!notificationStateList.has(newNotificationState.type)) {
                notificationStateList.set(
                    newNotificationState.type,
                    this.getState(
                        newNotificationState
                    )
                );
            }
        });
        return notificationStateList;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/utils/StateListFactory.ts
 **/